/**
 * Small domain vocabularies shared by many screens: weights, timeframes, statuses, exit reasons.
 * Labels only — every figure comes from the API.
 */

/** The cap on every user-authored name — `STRATEGY_NAME_MAX` on the server, `kNameMaxLength` on the phone. */
export const NAME_MAX_LENGTH = 50;

export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '2h', '4h', '12h', '1d'] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

export const TIMEFRAME_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '12h': 43200,
  '1d': 86400,
};

/** Timeframes the plan allows. Empty + unrestricted means all of them (EntitlementsOut). */
export function allowedTimeframes(ent: { timeframes: string[]; timeframes_restricted: boolean } | undefined): Set<string> {
  if (!ent || !ent.timeframes_restricted) return new Set(TIMEFRAMES);
  return new Set(ent.timeframes);
}

/**
 * The timeframes a chart of one instrument can show: the plan's, intersected with what the
 * instrument has (`capabilities.timeframes` — no 12h on a stock, docs/equities_design.md D4). A
 * condition's own timeframe picker stays plan-level: a strategy is not tied to one instrument.
 */
export function chartTimeframes(
  ent: { timeframes: string[]; timeframes_restricted: boolean } | undefined,
  instrument: { capabilities?: { timeframes?: string[] } | null } | undefined,
): Set<string> {
  const plan = allowedTimeframes(ent);
  const own = instrument?.capabilities?.timeframes;
  if (!own || own.length === 0) return plan;
  return new Set([...plan].filter((tf) => own.includes(tf)));
}

/** `choice` when the chart can show it; otherwise 1h, or the first it can — so a choice made on
 *  EUR/USD (12h) does not survive switching the chart to AAPL. */
export function usableTimeframe(choice: string, allowed: Set<string>): string {
  if (allowed.has(choice)) return choice;
  if (allowed.has('1h')) return '1h';
  return [...allowed][0] ?? choice;
}

export interface WeightPreset {
  name: string;
  weight: string;
}

/** The preset a stored weight is (weights are always snapped to a preset server-side). */
export function weightName(weight: string | number | null | undefined, presets: WeightPreset[] | undefined): string {
  if (weight == null) return '';
  const w = Number(weight);
  const hit = presets?.find((p) => Number(p.weight) === w);
  return hit?.name ?? String(w);
}

/** "≈ N% of score": a condition's share of the strategy's total weight. Describes the weights. */
export function weightShare(weight: string | number, all: Array<string | number>): number {
  const total = all.reduce<number>((a, w) => a + Number(w), 0);
  return total > 0 ? (Number(weight) / total) * 100 : 0;
}

const EXIT_REASONS: Record<string, string> = {
  stop_loss: 'Stop loss',
  // A stop that fired after trailing off its entry level; one that never moved is a stop loss.
  trailing_stop: 'Trailing stop',
  breakeven_stop: 'Break-even stop',
  take_profit: 'Take profit',
  exit_rule: 'Exit condition',
  time_stop: 'Time limit',
  end_of_history: 'End of history',
};

export const exitReasonLabel = (r: string | null | undefined) => (r ? (EXIT_REASONS[r] ?? r.replace(/_/g, ' ')) : '');

/**
 * How an exit's stop trails, for a summary line: "trailing", or with a swing trail
 * "trailing · steps to 15m swings (3 bars, 0.25× ATR)"; '' for a stop that does not trail.
 */
export function trailLabel(e: { trail_stop?: boolean | null; trail_swing?: { timeframe: string; pivot_length: number; buffer_atr: number } | null }): string {
  if (!e.trail_stop) return '';
  const w = e.trail_swing;
  return w ? `trailing · steps to ${w.timeframe} swings (${w.pivot_length} bars, ${w.buffer_atr}× ATR)` : 'trailing';
}

export const ASSET_GROUPS: Array<{ key: string; label: string }> = [
  { key: 'forex', label: 'Forex' },
  { key: 'metal', label: 'Metals' },
  { key: 'crypto', label: 'Crypto' },
  // US equities split by `instrument_kind` (docs/equities_design.md §10.4): one asset type, two chips.
  { key: 'stock', label: 'Stocks' },
  { key: 'etf', label: 'ETFs' },
];

/** ISO 4217's precious-metal codes (X-prefixed), so XAU/EUR is a metal as much as XAU/USD. */
const METAL_CODES = new Set(['XAU', 'XAG', 'XPT', 'XPD']);

/**
 * The browsing category, read off the symbol for metals — the phone's `isMetal`.
 *
 * **Not `asset_type` alone, which says `forex` for gold, silver and platinum.** That is correct
 * server-side (it drives market hours and the candle bucket grid; metals trade the forex session),
 * but as a browsing category it filed the metals under Forex, where nobody looks for them.
 */
export function assetGroupLabel(i: { asset_type: string; symbol: string; instrument_kind?: string | null }): string {
  if (METAL_CODES.has((i.symbol.split('/')[0] ?? '').toUpperCase())) return 'Metals';
  if (i.asset_type === 'crypto') return 'Crypto';
  // Stocks and ETFs share `equity` (one session, feed and cost model); the kind is the browsing split.
  if (i.asset_type === 'equity') return i.instrument_kind === 'etf' ? 'ETFs' : 'Stocks';
  if (i.asset_type === 'forex') return 'Forex';
  return i.asset_type;
}

/**
 * The most-traded instruments first, for choosing what a chart opens on. The equities follow the
 * original ten, so an Elite account's charts keep opening where they did and a stocks-only
 * watchlist still opens on SPY rather than on the alphabetically first ticker.
 */
export const POPULAR_SYMBOLS = [
  'BTC/USD', 'EUR/USD', 'ETH/USD', 'XAU/USD', 'GBP/USD', 'USD/JPY', 'SOL/USD', 'AUD/USD', 'USD/CAD', 'XRP/USD',
  'SPY', 'AAPL', 'QQQ', 'NVDA', 'TSLA',
];

/**
 * The instrument a chart should open on: the most popular one the viewer's plan allows, preferring
 * one from `among` (say, where the strategy is applied) so the chart shows a market it watches.
 *
 * **Popularity before order.** Taking the first of `among` opened editing on whichever instrument
 * the API listed first — USD/CNH for a strategy applied to a forex list — when EUR/USD was on the
 * same list. Locked instruments (plan or country) are never chosen: a chart the viewer cannot load
 * is a worse opening than a less relevant one.
 */
export function preferredInstrument<T extends { id: string; symbol: string; available?: boolean | null }>(
  instruments: T[],
  among?: ReadonlyArray<string>,
): T | undefined {
  const open = instruments.filter((i) => i.available !== false);
  const rank = (i: T) => {
    const r = POPULAR_SYMBOLS.indexOf(i.symbol);
    return r < 0 ? POPULAR_SYMBOLS.length : r;
  };
  const best = (pool: T[]) => [...pool].sort((a, b) => rank(a) - rank(b) || a.symbol.localeCompare(b.symbol))[0];
  // A strategy applied only to exotics still opens on one of them: it is what the strategy watches.
  const scoped = among?.length ? open.filter((i) => among.includes(i.id)) : [];
  return best(scoped) ?? best(open);
}

export type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | string;

export function runStatusTone(status: RunStatus): { tone: 'brand' | 'pos' | 'neg' | 'neutral' | 'warn'; live: boolean; label: string } {
  switch (status) {
    case 'queued':
      return { tone: 'neutral', live: true, label: 'Queued' };
    case 'running':
      return { tone: 'brand', live: true, label: 'Running' };
    case 'completed':
      return { tone: 'pos', live: false, label: 'Completed' };
    case 'failed':
      return { tone: 'neg', live: false, label: 'Failed' };
    default:
      return { tone: 'neutral', live: false, label: status };
  }
}

/** Whether a run is still in flight (its page polls). */
export const isActive = (status: RunStatus) => status === 'queued' || status === 'running';

export const planLabel = (code: string | undefined) => (code ? code.charAt(0).toUpperCase() + code.slice(1) : '');

/**
 * Whether a served series belongs to a timeframe other than the chart's. A condition is drawn only
 * on the timeframe it is evaluated on (`indicator_strategy.md` §8, web_app_design.md §4): computed
 * on other bars it would be a different quantity from the line the alert read. Off the chart's
 * timeframe its legend entry stays, as the way back.
 */
export const isOffTimeframe = (ser: { rule_timeframe?: string | null }, chartTimeframe: string | undefined): boolean =>
  Boolean(chartTimeframe && ser.rule_timeframe && ser.rule_timeframe !== chartTimeframe);

/** Conditions per timeframe, for the picker's counts (`TimeframePicker.counts`). */
export function conditionsPerTimeframe(conditions: Array<{ timeframe?: string | null }>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of conditions) {
    if (!c.timeframe) continue;
    out[c.timeframe] = (out[c.timeframe] ?? 0) + 1;
  }
  return out;
}
